/**
 * RatingPicker - The three-tier rating control for one inspection item.
 *
 * Good / Needs Attention / Critical, plus the note field that Critical
 * requires. Tapping the selected tier again clears it, returning the item to
 * un-rated.
 *
 * Critical is special and the interaction reflects it: the finding emails
 * operations immediately and escalates to the branch manager if it stays open,
 * so it cannot be saved without a written reason. Tapping Critical shows the
 * button as selected and reveals the note field, but the save is deferred
 * until there is text — otherwise the server would 422 and the button would
 * flick back, which reads as the app losing the tap.
 *
 * The note also saves itself a moment after the inspector stops typing, not
 * only when the field loses focus. Beta testers rated items Critical, typed
 * the reason and then looked for **Continue** — which stays hidden until every
 * required item is rated, and a pending Critical isn't rated until it saves.
 * Waiting for a blur that never came read as "you cannot close out an
 * inspection with anything other than Good".
 *
 * This lived twice — once in the inspection detail tab, once in the field
 * workflow — with the two copies already drifting. One implementation, both
 * surfaces, and the tier vocabulary itself comes from utils/inspectionRating.
 *
 * @example
 * <RatingPicker
 *	 rating={item.rating}
 *	 note={item.ratingNote}
 *	 onRate={(rating, note) => rateItem(item.id, rating, note)}
 * />
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { AutoGrowTextarea } from '../../atoms/AutoGrowTextarea';
import { NoteBlurBridge } from '../NoteBlurBridge';
import { INSPECTION_RATINGS, ratingMeta } from '../../../utils/inspectionRating';
import type { InspectionRating } from '../../../utils/inspectionRating';

/** How long the note waits after the last keystroke before saving itself. */
export const NOTE_AUTOSAVE_MS = 800;

/* ============================================
 * TYPE DEFINITIONS
 * ============================================ */

export interface RatingPickerProps {
	/** Currently persisted tier, or null when the item is un-rated. */
	rating: string | null | undefined;
	/** Currently persisted note (the reason for the tier). */
	note?: string | null;
	/**
	 * Persist a rating change. Called with null to clear. For Critical the
	 * note is guaranteed non-empty — the picker holds the save until then.
	 */
	onRate: (rating: InspectionRating | null, note: string | null) => Promise<void>;
	/** Read-only mode — used once an inspection is closed for editing. */
	disabled?: boolean;
	/** Optional extra classes merged onto the root. */
	className?: string;
}

/* ============================================
 * COMPONENT
 * ============================================ */

export const RatingPicker: React.FC<RatingPickerProps> = ({
	rating,
	note,
	onRate,
	disabled = false,
	className = '',
}) => {
	const persistedNote = note ?? '';
	const [noteDraft, setNoteDraft] = useState<string>(persistedNote);
	const lastSavedNoteRef = useRef<string>(persistedNote);

	/* Re-sync when the server sends a different value (a refetch, or another
	   user editing the same inspection) — but only while the field holds
	   nothing unsaved. The note autosaves mid-typing, so the server echo of
	   "Water on" can land after the inspector has typed "Water on the
	   landing"; adopting it would eat the words typed since. */
	useEffect(() => {
		setNoteDraft((draft) => (draft === lastSavedNoteRef.current ? persistedNote : draft));
		lastSavedNoteRef.current = persistedNote;
	}, [persistedNote]);

	/* Critical tapped but no reason written yet. The button renders as selected
	   so the tap is acknowledged, while the actual save waits for text. */
	const [pendingCritical, setPendingCritical] = useState(false);

	/* Once the server confirms critical, the pending state is over. */
	useEffect(() => {
		if (rating === 'critical') setPendingCritical(false);
	}, [rating]);

	const isCriticalSelected = rating === 'critical' || pendingCritical;

	/** Whether a tier button should render as selected. */
	const isSelected = (value: InspectionRating): boolean =>
		value === 'critical' ? isCriticalSelected : rating === value;

	/* Pending typing-autosave (see handleNoteChange below). Declared up here
	   because picking a tier also sends the note, which makes a queued
	   autosave redundant. */
	const autosaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const pendingTextRef = useRef<string | null>(null);

	const cancelAutosave = () => {
		if (autosaveTimerRef.current) clearTimeout(autosaveTimerRef.current);
		autosaveTimerRef.current = null;
		pendingTextRef.current = null;
	};

	const handlePick = useCallback(async (next: InspectionRating) => {
		if (disabled) return;
		cancelAutosave();

		// Tapping the current tier clears it.
		const finalRating = rating === next ? null : next;

		if (finalRating === 'critical') {
			const trimmed = noteDraft.trim();
			if (!trimmed) {
				// Show it as selected, reveal the note field, defer the save.
				setPendingCritical(true);
				return;
			}
			/* Same as commitNote: stay pending until the save settles. */
			await onRate('critical', trimmed);
			setPendingCritical(false);
			return;
		}

		setPendingCritical(false);
		// Non-critical tiers keep whatever reason was written — a "needs
		// attention" item benefits from context too, it just isn't required.
		await onRate(finalRating, finalRating ? (noteDraft.trim() || null) : null);
	}, [disabled, rating, noteDraft, onRate]);

	/**
	 * Commit the note. Runs on blur and from the typing autosave. This is also
	 * where a pending Critical becomes a real save, now that there is something
	 * to escalate with.
	 *
	 * @param text     The note to commit (passed in, not read from state, so
	 *                 the autosave timer commits what was typed when it fired).
	 * @param fromBlur True when focus has left the field. Only a blur may
	 *                 cancel a pending Critical — mid-typing, an empty field
	 *                 just means the inspector hasn't written anything yet.
	 */
	const commitNote = useCallback(async (text: string, fromBlur: boolean) => {
		if (disabled) return;
		const trimmed = text.trim();

		if (pendingCritical) {
			if (!trimmed) {
				// Opened the note but wrote nothing — treat the Critical tap
				// as cancelled rather than saving something unactionable.
				if (fromBlur) setPendingCritical(false);
				return;
			}
			lastSavedNoteRef.current = trimmed;
			/* Stay pending until the save settles. Clearing first left a beat
			   with no tier selected (the parent's rating update lands a tick
			   later), so the note field vanished and the row shrank — a tap on
			   the next item landed on the space it vacated and was lost, and an
			   autosave mid-typing took the field (and focus) away. Once it
			   settles: the rating effect has already cleared pending if the
			   save worked; if it didn't, this drops back to unselected. */
			await onRate('critical', trimmed);
			setPendingCritical(false);
			return;
		}

		if (!rating || trimmed === lastSavedNoteRef.current) return;

		// An already-critical item cannot have its reason erased — that is the
		// server's rule too, so snap back (on blur) rather than send a doomed
		// request.
		if (rating === 'critical' && !trimmed) {
			if (fromBlur) setNoteDraft(lastSavedNoteRef.current);
			return;
		}

		lastSavedNoteRef.current = trimmed;
		await onRate(rating as InspectionRating, trimmed || null);
	}, [disabled, pendingCritical, rating, onRate]);

	/* Typing autosave. The timer calls through a ref so it always runs the
	   latest commitNote (the rating or pending state may have changed while
	   it waited). */
	const commitNoteRef = useRef(commitNote);
	commitNoteRef.current = commitNote;
	const handleNoteChange = (value: string) => {
		setNoteDraft(value);
		cancelAutosave();
		pendingTextRef.current = value;
		autosaveTimerRef.current = setTimeout(() => {
			autosaveTimerRef.current = null;
			pendingTextRef.current = null;
			commitNoteRef.current(value, false);
		}, NOTE_AUTOSAVE_MS);
	};

	/* If the row goes away mid-wait (the inspector moved to the next step),
	   save what was typed rather than dropping it. */
	useEffect(() => () => {
		const text = pendingTextRef.current;
		cancelAutosave();
		if (text !== null) commitNoteRef.current(text, false);
	}, []);

	const handleNoteBlur = useCallback(() => {
		cancelAutosave();
		commitNote(noteDraft, true);
	}, [commitNote, noteDraft]);

	/* The note field is shown for Critical (required) and Needs Attention
	   (useful context for whoever picks the follow-up up). Good needs no
	   explanation, so the field stays out of the way. */
	const showNote = isCriticalSelected || rating === 'needs_attention';
	const noteRequired = isCriticalSelected;
	const activeMeta = ratingMeta(rating) ?? (pendingCritical ? ratingMeta('critical') : null);

	return (
		<div className={`rating-picker ${className}`} data-ref="molecule-rating-picker">
			{/* --- Tier buttons --- */}
			<div className="rating-picker__options flex flex-wrap gap-6px" role="group" aria-label="Item rating">
				{INSPECTION_RATINGS.map((meta) => {
					const selected = isSelected(meta.value);
					return (
						<button
							key={meta.value}
							type="button"
							onClick={() => handlePick(meta.value)}
							disabled={disabled}
							aria-pressed={selected}
							title={meta.action}
							className={[
								'rating-picker__option',
								selected ? 'rating-picker__option--selected' : '',
								'flex-1 min-w-[96px] px-10px py-8px rounded-md border text-13px font-sans font-semibold',
								'min-h-44px transition-colors disabled:opacity-50 disabled:cursor-not-allowed',
								selected ? meta.selectedClasses : meta.unselectedClasses,
							].filter(Boolean).join(' ')}
						>
							{meta.label}
						</button>
					);
				})}
			</div>

			{/* --- What the chosen tier means ---
			    Shown under the buttons so the person rating knows what they are
			    committing someone to, rather than having to remember. */}
			{activeMeta && (
				<p className="rating-picker__action mt-6px text-12px font-sans text-brand-text-muted">
					{activeMeta.action}
				</p>
			)}

			{/* --- Reason --- */}
			{showNote && (
				<div className="rating-picker__note mt-8px">
					<label className="block text-12px font-sans font-medium text-brand-text-muted mb-4px">
						{noteRequired ? 'What is the issue? (required)' : 'Notes (optional)'}
					</label>
					<AutoGrowTextarea
						value={noteDraft}
						onChange={handleNoteChange}
						disabled={disabled}
						minRows={2}
						placeholder={noteRequired
							? 'Describe the issue — this is sent to management.'
							: 'Anything the follow-up crew should know.'}
						className={noteRequired && !noteDraft.trim() ? 'border-status-error' : ''}
					/>
					{/* The atom stays unaware of save-on-blur; this sibling fires
					    once focus leaves the group entirely. */}
					<NoteBlurBridge onBlur={handleNoteBlur} />
					{noteRequired && !noteDraft.trim() && (
						<p className="rating-picker__note-hint mt-4px text-12px font-sans text-status-error">
							A critical finding is emailed to management — describe the issue so it can be actioned.
						</p>
					)}
				</div>
			)}
		</div>
	);
};

export default RatingPicker;
